// src/pages/Dashboard/Admin/PagesPersonnalisees/components/PageArticlesDrawer.jsx

import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaImage,
  FaLayerGroup,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  addArticle,
  deleteArticle,
  getAllArticlesByPage,
  updateArticle,
} from "../../../../../services/articlesPagesServices";

import { toast } from "react-toastify";

import {
  PAGE_FALLBACK_IMAGE,
  QUILL_FORMATS,
  QUILL_MODULES,
  buildArticlePayload,
  getDefaultArticleForm,
  getPageTitle,
  mapArticleToForm,
  sortPageArticles,
  stripHtml,
} from "../utils/pagesDashboardUtils";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function ActionButton({ title, onClick, disabled, children, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    info: "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.base
      }`}
    >
      {children}
    </button>
  );
}

export default function PageArticlesDrawer({ isOpen, page, onClose, onUpdated }) {
  const [articles, setArticles] = useState([]);
  const [formData, setFormData] = useState(getDefaultArticleForm());
  const [editingArticle, setEditingArticle] = useState(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [openPreviewId, setOpenPreviewId] = useState(null);

  const pageId = page?.id;

  const nextOrder = useMemo(() => {
    const max = articles.reduce(
      (acc, article) => Math.max(acc, Number(article?.ordre || 0)),
      0
    );

    return max + 1;
  }, [articles]);

  const resetForm = useCallback(() => {
    setFormData(getDefaultArticleForm(nextOrder));
    setEditingArticle(null);
  }, [nextOrder]);

  const loadArticles = useCallback(async () => {
    if (!pageId) return;

    try {
      setLoading(true);

      const result = await getAllArticlesByPage(pageId);
      setArticles(sortPageArticles(result));
    } catch (error) {
      console.error("Erreur chargement articles page :", error);
      toast.error("Erreur lors du chargement des articles de la page.");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    if (!isOpen || !pageId) return;

    setArticles([]);
    setEditingArticle(null);
    setOpenPreviewId(null);
    setFormData(getDefaultArticleForm(1));

    loadArticles();
  }, [isOpen, pageId, loadArticles]);

  useEffect(() => {
    if (!isOpen || editingArticle) return;

    setFormData((prev) => ({
      ...prev,
      ordre: prev.ordre || nextOrder,
    }));
  }, [isOpen, editingArticle, nextOrder]);

  const canSubmit =
    !submitting &&
    String(formData.titre_article || "").trim().length > 0 &&
    pageId;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setFormData(mapArticleToForm(article, nextOrder));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    try {
      setSubmitting(true);

      const payload = buildArticlePayload(
        formData,
        pageId,
        editingArticle?.id || null
      );

      if (editingArticle?.id) {
        await updateArticle(payload);
        toast.success("Article mis à jour.");
      } else {
        await addArticle(payload);
        toast.success("Article ajouté.");
      }

      await loadArticles();
      await onUpdated?.();
      setEditingArticle(null);
      setFormData(getDefaultArticleForm(nextOrder + 1));
    } catch (error) {
      console.error("Erreur enregistrement article page :", error);
      toast.error("Erreur lors de l’enregistrement de l’article.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteArticle = async (article) => {
    if (!article?.id) return;

    if (
      !window.confirm(
        `Supprimer l’article « ${article.titre_article || "sans titre"} » ?`
      )
    ) {
      return;
    }

    try {
      setDeletingId(article.id);

      await deleteArticle(article.id);

      toast.success("Article supprimé.");
      await loadArticles();
      await onUpdated?.();

      if (editingArticle?.id === article.id) {
        setEditingArticle(null);
        setFormData(getDefaultArticleForm(nextOrder));
      }
    } catch (error) {
      console.error("Erreur suppression article page :", error);
      toast.error("Erreur lors de la suppression de l’article.");
    } finally {
      setDeletingId(null);
    }
  };

  const togglePreview = (articleId) => {
    setOpenPreviewId((prev) => (prev === articleId ? null : articleId));
  };

  if (!isOpen || !page) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Articles de page
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaLayerGroup className="text-indigo-600 dark:text-indigo-300" />
              {getPageTitle(page)}
            </h2>

            <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ajoutez plusieurs blocs de contenu à votre page personnalisée :
              titre, texte riche, image et ordre d’affichage.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                <FaPlus className="text-indigo-600 dark:text-indigo-300" />
                {editingArticle ? "Modifier un article" : "Ajouter un article"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-4">
              <div className="grid gap-4 lg:grid-cols-[1fr_140px]">
                <Field label="Titre de l’article *">
                  <input
                    type="text"
                    name="titre_article"
                    value={formData.titre_article}
                    onChange={handleChange}
                    placeholder="Ex : Le mot du président"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>

                <Field label="Ordre">
                  <input
                    type="number"
                    name="ordre"
                    value={formData.ordre}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>
              </div>

              <Field label="Contenu">
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                  <ReactQuill
                    value={formData.contenu_texte}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        contenu_texte: value,
                      }))
                    }
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    className="[&_.ql-container]:min-h-[220px] [&_.ql-container]:max-h-[420px] [&_.ql-container]:overflow-y-auto dark:[&_.ql-toolbar]:border-slate-700 dark:[&_.ql-container]:border-slate-700"
                  />
                </div>
              </Field>

              <div className="grid gap-4 lg:grid-cols-[1fr_180px]">
                <Field
                  label="URL image"
                  hint="Image optionnelle affichée avec l’article."
                >
                  <div className="relative">
                    <FaImage className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                    <input
                      type="text"
                      name="url_image"
                      value={formData.url_image}
                      onChange={handleChange}
                      placeholder="https://... ou /uploads/..."
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />
                  </div>
                </Field>

                <div>
                  <p className="mb-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                    Aperçu image
                  </p>

                  <div className="h-24 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                    {formData.url_image ? (
                      <img
                        src={formData.url_image}
                        alt="Aperçu"
                        className="h-full w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.src = PAGE_FALLBACK_IMAGE;
                        }}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-3 text-center text-xs text-slate-500 dark:text-slate-400">
                        Aucun visuel
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {editingArticle ? (
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <FaTimes />
                    Annuler modification
                  </button>
                ) : null}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                >
                  <FaSave />
                  {submitting
                    ? "Enregistrement..."
                    : editingArticle
                    ? "Enregistrer"
                    : "Ajouter l’article"}
                </button>
              </div>
            </form>
          </section>

          <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                Articles enregistrés
              </p>
            </div>

            {loading ? (
              <div className="space-y-3 p-4">
                {[...Array(4)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                  />
                ))}
              </div>
            ) : articles.length ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] table-fixed">
                  <colgroup>
                    <col className="w-[80px]" />
                    <col className="w-[280px]" />
                    <col className="w-[120px]" />
                    <col className="w-[260px]" />
                    <col className="w-[180px]" />
                  </colgroup>

                  <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
                    <tr>
                      <th className="px-3 py-3 text-left font-bold">Ordre</th>
                      <th className="px-3 py-3 text-left font-bold">Article</th>
                      <th className="px-3 py-3 text-left font-bold">Image</th>
                      <th className="px-3 py-3 text-left font-bold">Contenu</th>
                      <th className="px-3 py-3 text-right font-bold">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                    {articles.map((article) => {
                      const isPreviewOpen = openPreviewId === article.id;
                      const contentPreview = stripHtml(article.contenu_texte);

                      return (
                        <Fragment key={article.id}>
                          <tr className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-3 py-4 align-top">
                              <span className="inline-flex rounded-xl bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                {article.ordre ?? "—"}
                              </span>
                            </td>

                            <td className="px-3 py-4 align-top">
                              <p className="font-bold text-slate-900 dark:text-white">
                                {article.titre_article || "Sans titre"}
                              </p>
                            </td>

                            <td className="px-3 py-4 align-top">
                              <div className="h-14 w-20 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                                {article.url_image ? (
                                  <img
                                    src={article.url_image}
                                    alt={article.titre_article || "Article"}
                                    className="h-full w-full object-cover"
                                    onError={(event) => {
                                      event.currentTarget.src =
                                        PAGE_FALLBACK_IMAGE;
                                    }}
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-xs text-slate-400">
                                    —
                                  </div>
                                )}
                              </div>
                            </td>

                            <td className="px-3 py-4 align-top">
                              {contentPreview ? (
                                <p
                                  className="line-clamp-2 text-xs leading-5 text-slate-600 dark:text-slate-300"
                                  title={contentPreview}
                                >
                                  {contentPreview}
                                </p>
                              ) : (
                                <p className="text-xs text-slate-400">
                                  Aucun contenu
                                </p>
                              )}

                              <button
                                type="button"
                                onClick={() => togglePreview(article.id)}
                                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-indigo-700 underline underline-offset-2 hover:opacity-80 dark:text-indigo-300"
                              >
                                {isPreviewOpen ? <FaEyeSlash /> : <FaEye />}
                                {isPreviewOpen ? "Masquer" : "Voir l’aperçu"}
                              </button>
                            </td>

                            <td className="px-3 py-4 align-top">
                              <div className="flex items-center justify-end gap-1.5">
                                <ActionButton
                                  title="Modifier"
                                  onClick={() => handleEditArticle(article)}
                                  tone="primary"
                                >
                                  <FaEdit />
                                </ActionButton>

                                <ActionButton
                                  title="Supprimer"
                                  onClick={() => handleDeleteArticle(article)}
                                  disabled={deletingId === article.id}
                                  tone="danger"
                                >
                                  <FaTrash />
                                </ActionButton>
                              </div>
                            </td>
                          </tr>

                          {isPreviewOpen ? (
                            <tr>
                              <td colSpan={5} className="bg-slate-50 p-4 dark:bg-slate-900">
                                <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                                  {article.url_image ? (
                                    <img
                                      src={article.url_image}
                                      alt={article.titre_article || "Article"}
                                      className="mb-4 max-h-56 rounded-2xl object-contain ring-1 ring-slate-200 dark:ring-slate-700"
                                      onError={(event) => {
                                        event.currentTarget.src =
                                          PAGE_FALLBACK_IMAGE;
                                      }}
                                    />
                                  ) : null}

                                  <div
                                    className="prose prose-sm max-w-none dark:prose-invert"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        article.contenu_texte ||
                                        "<p>Aucun contenu.</p>",
                                    }}
                                  />
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-10 text-center">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Aucun article pour cette page.
                </p>
              </div>
            )}
          </section>
        </div>

        <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}